// hubSort.js — date/sort helpers for the Community Hub list (HubPage.js).
//
// Extracted so the sort logic is unit-testable without pulling in firebase/
// react. The key one is `toMillis`: a published item's date reaches the client
// in several shapes, and the old `new Date(value)` path silently broke on
// Firestore Timestamp objects (-> Invalid Date -> NaN), which made
// `?sort=newest` fall back to document-id order (reads as alphabetical).

// Robust timestamp -> epoch-millis. Handles:
//   - ISO 8601 string      "2026-06-23T10:00:00Z"   (seed + REST publish)
//   - Firestore Timestamp  {seconds,nanoseconds} / {_seconds} / .toMillis()
//   - epoch number         1719136800000
//   - Date
// Returns 0 for missing/unparseable values.
export function toMillis(value) {
  if (value == null) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? 0 : t;
  }
  if (typeof value === 'object') {
    if (typeof value.toMillis === 'function') {
      const t = value.toMillis();
      return Number.isFinite(t) ? t : 0;
    }
    const secs = value.seconds ?? value._seconds;
    if (typeof secs === 'number') {
      const nanos = value.nanoseconds ?? value._nanoseconds ?? 0;
      return secs * 1000 + Math.floor(nanos / 1e6);
    }
    return 0;
  }
  if (typeof value === 'string') {
    const t = new Date(value).getTime();
    return Number.isNaN(t) ? 0 : t;
  }
  return 0;
}

// Best-available publish instant for an item (publishedAt, else updatedAt).
export function publishMillis(p) {
  return toMillis(p.publishedAt) || toMillis(p.updatedAt);
}

// Deterministic tiebreak key so equal-timestamp items (e.g. pre-backfill items
// all at 0) sort stably instead of in arbitrary document-id order.
export function sortKeyTiebreak(p) {
  return String(p.slug || p.packageId || p._id || p.name || '');
}

// Newest-first comparator: real date desc, then stable slug order.
export function byNewest(a, b) {
  const dt = publishMillis(b) - publishMillis(a);
  if (dt !== 0) return dt;
  return sortKeyTiebreak(a).localeCompare(sortKeyTiebreak(b));
}
